import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportApi } from '../services/api';
import TrackCompanyDialog from './TrackCompanyDialog';
import './SearchDiscover.css';
import { discoveryPreviewRows } from './discoveryPreview';
import CompanySearch, { searchableCompanies } from './CompanySearch';

const hashTicker = () => window.location.hash.slice(1).toUpperCase();
const locationQuery = () => hashTicker() ? '' : new URLSearchParams(window.location.search).get('q') || '';

export const relatedCompanies = (center, names, groups) => [...new Set([
  ...(groups[center.group]?.members || []),
  ...[...names.values()].filter((row) => row.group === center.group).map((row) => row.ticker),
  ...(center.neighbors || []), ...(center.rivals || [])
])].filter((ticker) => ticker !== center.ticker && names.has(ticker));

const SearchDiscover = ({ doc, onOpenCompany, publicMode = false, isEmployee = false, searchTarget = null }) => {
  const [query, setQuery] = useState(locationQuery);
  const [selected, setSelected] = useState(hashTicker);
  const [company, setCompany] = useState(null);
  const [added, setAdded] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [directoryError, setDirectoryError] = useState('');
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const rail = useRef(null);
  const selectedCategory = useRef(null);
  const summary = useRef(null);
  const rows = useMemo(() => discoveryPreviewRows((doc.sections || []).flatMap((section) => section.rows || [])), [doc.sections]);
  const names = new Map(rows.map((row) => [row.ticker, row]));
  const groups = doc.data?.groups || {};
  const groupNames = [...new Set(rows.map((row) => row.group).filter(Boolean))].sort();
  const allNames = searchableCompanies(rows, directory);
  const tracking = new Set([...(doc.access?.held || []), ...added].map((ticker) => ticker.toUpperCase()));
  const center = allNames.get(selected) || names.get('ANT') || rows[0];
  const groupLayoutKey = JSON.stringify(groupNames);
  useEffect(() => {
    let active = true;
    reportApi.companyDirectory().then((result) => {
      if (active) { setDirectory(result.companies); setDirectoryLoading(false); }
    }).catch((err) => {
      if (active) { setDirectoryError(err.message); setDirectoryLoading(false); }
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const strip = rail.current, chip = selectedCategory.current;
    if (!strip || !chip) return;
    const top = chip.getBoundingClientRect().top - strip.getBoundingClientRect().top + strip.scrollTop - strip.clientTop;
    if (top < strip.scrollTop || top + chip.offsetHeight > strip.scrollTop + strip.clientHeight) {
      strip.scrollTo?.({ top: Math.max(0, top - strip.clientHeight / 2 + chip.offsetHeight / 2), behavior: 'smooth' });
    }
  }, [center?.group, groupLayoutKey]);
  useEffect(() => {
    const changed = () => {
      setSelected(hashTicker());
      setQuery(locationQuery());
    };
    window.addEventListener('hashchange', changed);
    window.addEventListener('popstate', changed);
    return () => {
      window.removeEventListener('hashchange', changed);
      window.removeEventListener('popstate', changed);
    };
  }, []);
  const focus = (ticker) => {
    if (!allNames.has(ticker)) return;
    setSelected(ticker); setQuery('');
    if (hashTicker() !== ticker) window.location.hash = ticker;
    summary.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  const open = (ticker, product) => {
    if (onOpenCompany) onOpenCompany(ticker, product);
    else window.location.assign(`/company/${encodeURIComponent(ticker)}?product=${product}`);
  };
  const action = (product, label) => {
    if (center.preview) return <p className="muted">Demo company - report and tracking actions disabled.</p>;
    if (isEmployee || (product === 'firstlook' && tracking.has(center.ticker))) {
      return <a className="primary-button" href={`/company/${encodeURIComponent(center.ticker)}?product=${product}`}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0 || !onOpenCompany) return;
          event.preventDefault(); open(center.ticker, product);
        }}>Open {label}</a>;
    }
    return <button className="primary-button" onClick={() => setCompany({ ...center, product })}>Add {label}</button>;
  };
  const related = center && !center.uncovered ? relatedCompanies(center, names, groups) : [];
  const search = <>
    <CompanySearch companies={allNames} onSelect={focus} query={query} onQueryChange={setQuery} />
    <p className="muted" role="status">{directoryLoading ? 'Loading US-listed company directory...' : `${allNames.size} companies searchable. ${rows.length} covered.`}</p>
    {directoryError && <p className="error-box" role="alert">US-listed company search is unavailable: {directoryError}. You can still browse covered companies.</p>}
  </>;
  return <div className="search-discover">
    {rows.some((row) => row.preview) && <p role="note">Local layout preview: 60 dummy groups added. Remove ?previewGroups=1 from the URL to return to real data.</p>}
    {searchTarget ? createPortal(search, searchTarget) : search}
    <div className="discovery-layout">
      <nav className="discovery-categories" aria-label="All business groups" ref={rail}>
        <h2>Browse categories</h2>
        {groupNames.map((name) => {
          const members = rows.filter((row) => row.group === name);
          const lead = names.has(groups[name]?.lead) ? groups[name].lead : members[0]?.ticker;
          return <button key={name} ref={name === center?.group ? selectedCategory : null}
            className={name === center?.group ? 'discovery-current-group' : ''}
            aria-pressed={name === center?.group} onClick={() => focus(lead)}>
            <span>{name}</span><small>{members.length} {members.length === 1 ? 'company' : 'companies'}</small>
          </button>;
        })}
      </nav>
      {center ? <>
        <article className="discovery-summary" ref={summary} aria-label={`Selected company ${center.ticker}`} aria-live="polite">
          <p className="eyebrow">{center.uncovered ? 'Not covered yet' : center.group}</p>
          <h1>{center.company || center.ticker}</h1>
          <div className="discovery-symbol-row"><strong className="discovery-ticker">{center.ticker}</strong>
            {tracking.has(center.ticker) && <span className="hub-badge">Tracking</span>}</div>
          {center.uncovered ? <>
            <p>We don&apos;t cover {center.company} yet. Request it, and we will email you when the research is ready.</p>
            <p className="muted">Or browse the categories on the left to find a company we already cover.</p>
          </> : <>
            <div className="discovery-pitch">{(center.pitch || 'A high-level summary has not been published for this company yet.').split(/\n\s*\n/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
            <h2>Related companies</h2>
            {related.length ? <table className="discovery-related" aria-label="Related companies"><tbody>
              {Array.from({ length: Math.ceil(related.length / 3) }, (_, index) => <tr key={index}>
                {related.slice(index * 3, index * 3 + 3).map((ticker) => <td key={ticker}>
                  <button onClick={() => focus(ticker)} aria-label={`Explore ${ticker}: ${names.get(ticker).company}`}>
                    <strong>{ticker}</strong><span>{names.get(ticker).company}</span>
                    {(center.rivals || []).includes(ticker) && <small>Rival</small>}
                  </button>
                </td>)}
              </tr>)}
            </tbody></table> : <p className="muted">No related covered companies have been published for {center.ticker}.</p>}
          </>}
        </article>
        <aside className="discovery-next" aria-label="Research options">
          {center.uncovered ? <>
            <p>Want research on {center.company}? Ask us to add it to our coverage.</p>
            <section className="discovery-option"><h2>Request New Coverage</h2>
              <p>We build the research and email you when it is ready. Your plan&apos;s coverage request allowance applies.</p>
              <a className="primary-button" href={`/?ticker=${encodeURIComponent(center.ticker)}#request-coverage`}>Request {center.ticker}</a>
            </section>
          </> : <>
            <p>Take the next step to learn more about this company by adding it to your coverage.</p>
            <section className="discovery-option discovery-firstlook"><h2>First Look</h2>
              <dl><dt>Jargon &amp; numbers</dt><dd>Plain English, few numbers</dd><dt>Best for</dt><dd>Deciding if the company is worth your time</dd></dl>
              {action('firstlook', 'First Look')}
            </section>
            <section className="discovery-option"><h2>Deep Dive</h2>
              <dl><dt>Jargon &amp; numbers</dt><dd>Translated jargon, lots of numbers</dd><dt>Best for</dt><dd>Diving deeper into the specifics and differentiators</dd></dl>
              {action('deepdive', 'Deep Dive')}
            </section>
            <p className="muted discovery-fine">Start with the First Look. Go to the Deep Dive when you want the numbers.</p>
          </>}
        </aside>
      </> : <p className="empty-state">No covered companies are available. Search for a US-listed company to request coverage.</p>}
    </div>
    {company && <TrackCompanyDialog company={company} product={company.product} publicMode={publicMode} onClose={() => setCompany(null)}
      onTracked={(ticker) => { setAdded((current) => [...current, ticker]); setCompany(null); open(ticker, company.product); }}
      onOpened={(ticker) => { setCompany(null); open(ticker, company.product); }} />}
  </div>;
};

export default SearchDiscover;
